import axios from 'axios';
import type { Effort, FuelSurchargeRateBasis } from '../types/Effort';
import type {
  FormLabourOvertimeAllocationRow,
  PersistedLabourEffort
} from '../utils/labourOvertimeDisplay';

const API_URL = process.env.REACT_APP_API_URL || '';

/** Payload for bulk-creating efforts on a quantified task (Other costs, etc.). */
export interface CreateEffortInput {
  taskId: number;
  type: string;
  description?: string;
  quantity?: number;
  rate?: number;
  unitOfMeasure?: string;
  date?: string;
  comments?: string;
  employeeName?: string;
}

export const effortService = {
  /**
   * Labour efforts for forms (DRM OT on clone tasks). Max 100 form ids per request.
   */
  getLabourEffortsByFormIds: async (formIds: string[]): Promise<PersistedLabourEffort[]> => {
    const unique = Array.from(new Set(formIds.map(String).filter(Boolean))).slice(0, 100);
    if (unique.length === 0) return [];
    const response = await axios.get<PersistedLabourEffort[]>(
      `${API_URL}/efforts/labour-by-form-ids`,
      { params: { formIds: unique.join(',') }, withCredentials: true }
    );
    return response.data ?? [];
  },

  /**
   * Per-submission DRM labour OT allocation rows (includes work lines without labour Effort).
   */
  getLabourOvertimeAllocationsByFormIds: async (
    formIds: string[]
  ): Promise<FormLabourOvertimeAllocationRow[]> => {
    const unique = Array.from(new Set(formIds.map(String).filter(Boolean))).slice(0, 100);
    if (unique.length === 0) return [];
    const response = await axios.get<FormLabourOvertimeAllocationRow[]>(
      `${API_URL}/efforts/labour-overtime-allocations-by-form-ids`,
      { params: { formIds: unique.join(',') }, withCredentials: true }
    );
    return response.data ?? [];
  },

  patchLabourOvertimeAllocation: async (
    allocationId: number,
    body: {
      labourStraightHours: number;
      labourOvertime15Hours: number;
      labourOvertime20Hours: number;
      expectedUpdatedAt?: string;
      manualReason?: string;
    }
  ): Promise<FormLabourOvertimeAllocationRow> => {
    const response = await axios.patch<FormLabourOvertimeAllocationRow>(
      `${API_URL}/efforts/labour-overtime-allocations/${allocationId}`,
      body,
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * Creates DRM FormLabourOvertimeAllocation rows when missing (same permission as bucket edit).
   * No-op when rows already exist.
   */
  ensureLabourOtAllocationsForForm: async (
    formId: string
  ): Promise<{ ok: true; created: boolean }> => {
    const response = await axios.post<{ ok: true; created: boolean }>(
      `${API_URL}/efforts/labour-overtime-allocations/ensure-for-form/${encodeURIComponent(String(formId).trim())}`,
      {},
      { withCredentials: true }
    );
    return response.data ?? { ok: true, created: false };
  },

  /** Form-level bucket targets; server redistributes bottom-up across all allocation rows. */
  applyFormLabourOvertimeBuckets: async (body: {
    formId: string;
    labourStraightHours: number;
    labourOvertime15Hours: number;
    labourOvertime20Hours: number;
    manualReason?: string;
    rowVersions?: Array<{ id: number; expectedUpdatedAt: string }>;
  }): Promise<{ allocations: FormLabourOvertimeAllocationRow[] }> => {
    const response = await axios.post<{ allocations: FormLabourOvertimeAllocationRow[] }>(
      `${API_URL}/efforts/labour-overtime-allocations/apply-form-buckets`,
      body,
      { withCredentials: true }
    );
    return response.data ?? { allocations: [] };
  },

  getTaskEfforts: async (taskId: number): Promise<Effort[]> => {
    const response = await axios.get<Effort[]>(`${API_URL}/tasks/quantified/${taskId}/efforts`);
    return response.data ?? [];
  },

  deleteEffortsByType: async (taskId: number, type: string): Promise<void> => {
    await axios.delete(
      `${API_URL}/tasks/quantified/${taskId}/efforts/type/${encodeURIComponent(type)}`
    );
  },

  createMultipleEfforts: async (efforts: CreateEffortInput[]): Promise<Effort[]> => {
    if (!efforts.length) return [];
    const taskId = efforts[0].taskId;
    const payload = efforts.map((e) => ({
      type: e.type,
      description: e.description,
      quantity: e.quantity,
      rate: e.rate,
      unitOfMeasure: e.unitOfMeasure,
      date: e.date,
      comments: e.comments,
      employeeName: e.employeeName,
    }));
    const response = await axios.post<Effort[]>(
      `${API_URL}/tasks/quantified/${taskId}/efforts`,
      { efforts: payload }
    );
    return response.data ?? [];
  },

  patchVehicleFuelSurcharge: async (
    effortId: number,
    payload: {
      fuelSurchargeApplied: boolean;
      fuelSurchargeRateBasis?: FuelSurchargeRateBasis;
    }
  ): Promise<Effort> => {
    const response = await axios.patch<Effort>(
      `${API_URL}/efforts/vehicle-fuel/${effortId}`,
      payload
    );
    return response.data;
  },
};

export default effortService;
